import { useEffect, useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { RaadLogo } from '@/components/RaadLogo';
import { LangToggle } from '@/components/LangToggle';
import { useAuth } from './useAuth';
import { InvalidCredentialsError } from './authClient';
import { ApiError } from '@/lib/apiClient';
import { WHATSAPP_URL } from '@/lib/contact';
import { COUNTRIES, DEFAULT_COUNTRY_ISO, countryByIso } from './countries';

/** The three states this form can surface, each its own i18n message. */
type LoginError = 'invalidCredentials' | 'invalidPhone' | 'network';

/** True when the viewer asked the OS to reduce motion — then the intro plays no
 *  animation and the final layout is shown at once. Read once. */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/** Route illustration behind the hero (canvas 1h). Decorative. REBRAND 28 Oct 2026 —
 *  the road + dashes + dots resolve through `--raad-primary`/`--raad-accent` (Raad Blue
 *  now; the amber status dot keeps its state colour). 🔴 The focal route + big vehicle
 *  dot are confined to the BOTTOM-RIGHT, and a radial MASK fades the whole art to
 *  transparent toward the top-left — so NO line or dot ever crosses the brand lockup,
 *  the headline, the paragraph or the feature points, whatever their length (the longer
 *  Somali copy included). The mask is content-independent, so it holds at every panel
 *  size and in both themes. */
function HeroBackdrop() {
  // Opaque only near the bottom-right corner, fading out across the text column.
  const fade =
    'radial-gradient(100% 100% at 100% 100%, #000 38%, rgba(0,0,0,0.35) 58%, transparent 72%)';
  return (
    <svg
      viewBox="0 0 800 800"
      className="pointer-events-none absolute inset-0 h-full w-full opacity-60"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      style={{ WebkitMaskImage: fade, maskImage: fade }}
    >
      {/* Route sweeping along the bottom and up the right edge — kept clear of the
          left/centre text block; the mask fades its left tail out entirely. */}
      <path d="M250 840 C430 770 530 710 610 610 S760 430 900 350" stroke="var(--raad-hero-road)" strokeWidth="10" fill="none" />
      <path d="M250 840 C430 770 530 710 610 610 S760 430 900 350" stroke="var(--raad-primary)" strokeWidth="2.5" fill="none" strokeDasharray="8 12" opacity="0.85" />
      <path d="M520 880 C580 740 680 650 860 560" stroke="var(--raad-hero-road)" strokeWidth="7" fill="none" />
      {/* The big vehicle dot + halo, parked in the bottom-right. */}
      <circle cx="610" cy="610" r="26" fill="var(--raad-primary)" opacity="0.12" />
      <circle cx="610" cy="610" r="9" fill="var(--raad-accent)" />
      <circle cx="748" cy="452" r="7" fill="var(--raad-warn)" />
      <circle cx="486" cy="742" r="7" fill="var(--raad-hero-rule)" />
    </svg>
  );
}

function FeatureDot({ label }: { label: string }) {
  return (
    <span className="flex items-center gap-2">
      <span className="size-1.5 rounded-full bg-raad-accent" />
      {label}
    </span>
  );
}

/**
 * The top-left brand lockup — REBRAND 28 Oct 2026. The arrows mark + the "Raad Track"
 * wordmark, with a small "by Raad Telematics" under it. All brand ARTWORK (the product
 * name and the endorsement line are proper nouns, identical in every language), so they
 * are rendered as marks, not translated strings. Theme-aware via the `--raad-wordmark-*`
 * tokens (navy/blue on light, white/light-blue on dark).
 */
function BrandLockup() {
  return (
    <div className="relative flex items-center gap-3">
      <RaadLogo size={44} title="Raad Track" />
      <div className="font-display leading-none">
        <div className="text-xl font-extrabold sm:text-2xl" aria-hidden="true">
          <span style={{ color: 'var(--raad-wordmark-raad)' }}>Raad</span>{' '}
          <span style={{ fontWeight: 500, color: 'var(--raad-wordmark-track)' }}>Track</span>
        </div>
        <div className="mt-1.5 text-[11px] font-medium tracking-wide text-raad-muted">
          by Raad Telematics
        </div>
      </div>
    </div>
  );
}

/**
 * Screen 1h — Login. The two-panel marketing layout and all of its copy are UNCHANGED
 * (Mahad, 28 Oct: "maintain what it currently is — don't change the wording; only the
 * fonts, the logo and what we were changing"). Only the rebrand is applied on top: the
 * new Raad Track lockup, the Raad Blue tokens (button, language pill, links), the hero
 * art re-coloured to the blue family, a pure-white light background, and a short intro
 * animation — the centred arrows mark pulses, then lifts to reveal the page. The form is
 * ALWAYS mounted (the splash is a decorative, pointer-transparent overlay), and reduced
 * motion shows the final layout at once. Every string still resolves through i18n.
 */
export function Login() {
  const { t } = useTranslation();
  const { state, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const phoneId = useId();
  const countryId = useId();
  const passwordId = useId();
  const errorId = useId();

  const [countryIso, setCountryIso] = useState(DEFAULT_COUNTRY_ISO);
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<LoginError | null>(null);
  // Intro splash: a centred pulsing mark over the (always-mounted) page for a beat,
  // then it lifts. Reduced motion skips it entirely.
  const [intro, setIntro] = useState<boolean>(() => !prefersReducedMotion());

  const from = (location.state as { from?: string } | null)?.from ?? '/';

  // Already signed in (e.g. bootstrap resolved, or a second tab) — leave login.
  // A staff principal navigates away too: the protected route it lands on is
  // gated by RequireAuth, which renders the in-place staff panel (17i). Staying
  // on the login form would strand a valid session with no way forward.
  useEffect(() => {
    if (state.status === 'authenticated' || state.status === 'staff') {
      navigate(from, { replace: true });
    }
  }, [state.status, from, navigate]);

  // The splash lifts after a beat. One-shot; cleared on unmount.
  useEffect(() => {
    if (!intro) return;
    const id = window.setTimeout(() => setIntro(false), 900);
    return () => window.clearTimeout(id);
  }, [intro]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      // Compose international form: <selected dial code> + the national digits as
      // typed. raad-api's normalisePhone (libphonenumber-js) collapses it to
      // canonical E.164. Verified against the real dependency: for Somalia this
      // is byte-identical to the old national-only submission — `+252`+`612345678`
      // and `+252`+`0612345678` both normalise to `+252612345678`. If the user
      // pasted their own `+` prefix, respect it and don't double the dial code.
      const typed = phone.trim();
      const wire = typed.startsWith('+')
        ? typed.replace(/\s+/g, '')
        : `+${countryByIso(countryIso).dial}${typed.replace(/\D/g, '')}`;
      await login(wire, password);
      // On success useAuth flips to authenticated and the effect above redirects.
    } catch (err) {
      // Three distinct outcomes, three honest messages — never blame the network
      // for a problem the network didn't cause. The login route validates with
      // zValidator, so a number `normalisePhone` rejects comes back as a 400
      // (ApiError.status === 400), NOT a 401 (which is wrong-credentials →
      // InvalidCredentialsError) and NOT a transport failure (fetch rejects, so
      // no ApiError at all). This path got more reachable the moment other
      // countries became selectable — a wrong-length number is now a live case.
      setError(
        err instanceof InvalidCredentialsError
          ? 'invalidCredentials'
          : err instanceof ApiError && err.status === 400
            ? 'invalidPhone'
            : 'network',
      );
      setSubmitting(false);
    }
  }

  const brand = t('brand.name');

  return (
    <main className="relative flex min-h-dvh flex-col bg-raad-login-bg text-raad-text lg:flex-row">
      {/* HERO — compact branded band on mobile, full marketing panel at lg. The panel
          keeps the `--raad-deep` surface (a very light blue-white on light); the form
          half inherits the pure-white `--raad-login-bg`. */}
      <section className="relative flex flex-col justify-between overflow-hidden bg-raad-login-panel px-5 pb-6 pt-8 sm:px-8 lg:flex-[1.2] lg:px-14 lg:py-12">
        <HeroBackdrop />
        <BrandLockup />

        <div className="relative mt-6 lg:mt-0">
          <h1 className="max-w-[520px] font-display text-2xl font-bold leading-tight sm:text-3xl lg:text-[44px]">
            {t('login.hero.headline')}
          </h1>
          <p className="mt-3 hidden max-w-[460px] text-[15px] leading-relaxed text-raad-muted lg:mt-4 lg:block">
            {t('login.hero.subtitle')}
          </p>
          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-xs font-medium text-raad-muted lg:mt-7">
            <FeatureDot label={t('login.hero.feature.tracking')} />
            <FeatureDot label={t('login.hero.feature.alerts')} />
            <FeatureDot label={t('login.hero.feature.history')} />
            <FeatureDot label={t('login.hero.feature.secure')} />
            <FeatureDot label={t('login.hero.feature.multilingual')} />
            <FeatureDot label={t('login.hero.feature.support')} />
          </div>
        </div>

        <p className="relative mt-6 hidden text-xs text-raad-faint lg:block">
          {t('login.hero.footer', { brand })}
        </p>
      </section>

      {/* FORM */}
      <section className="flex flex-1 items-start justify-center px-5 py-8 sm:items-center sm:px-8 lg:py-12">
        <div className="w-full max-w-[420px]">
          <div className="mb-6 flex justify-end lg:mb-7">
            <LangToggle />
          </div>

          <h2 className="font-display text-2xl font-bold sm:text-[28px]">{t('login.title')}</h2>
          <p className="mt-2 text-sm text-raad-muted">{t('login.subtitle')}</p>

          <form onSubmit={onSubmit} noValidate className="mt-7">
            <label
              htmlFor={phoneId}
              className="mb-2 block text-xs font-semibold uppercase tracking-[0.08em] text-raad-muted"
            >
              {t('login.phone.label')}
            </label>
            <div className="flex gap-2.5">
              {/* Native <select>: the most reliable country picker on a 360px
                  Android screen — the OS renders a full-screen wheel, it is
                  keyboard-operable for free (focus, type-ahead, arrows, Enter),
                  and it needs no JS to open. A custom combobox would risk both
                  the keyboard and the small-screen cases the brief calls out. */}
              <label htmlFor={countryId} className="sr-only">
                {t('login.phone.country')}
              </label>
              <select
                id={countryId}
                value={countryIso}
                onChange={(e) => setCountryIso(e.target.value)}
                dir="ltr"
                className="min-h-12 w-[104px] shrink-0 rounded-xl border border-raad-line-10 bg-raad-input px-2.5 text-[15px] font-semibold focus:border-raad-accent focus:outline-none"
              >
                {COUNTRIES.map((c) => (
                  <option key={c.iso} value={c.iso}>
                    {c.flag} +{c.dial} · {t(`login.country.${c.iso}`)}
                  </option>
                ))}
              </select>
              <input
                id={phoneId}
                type="tel"
                inputMode="tel"
                autoComplete="tel-national"
                dir="ltr"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                aria-invalid={error != null}
                aria-describedby={error ? errorId : undefined}
                className="min-h-12 flex-1 rounded-xl border border-raad-line-10 bg-raad-input px-4 text-base font-semibold placeholder:font-normal placeholder:text-raad-dim focus:border-raad-accent focus:outline-none"
              />
            </div>

            <label
              htmlFor={passwordId}
              className="mb-2 mt-5 block text-xs font-semibold uppercase tracking-[0.08em] text-raad-muted"
            >
              {t('login.password.label')}
            </label>
            <input
              id={passwordId}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('login.password.placeholder')}
              aria-invalid={error != null}
              aria-describedby={error ? errorId : undefined}
              className="min-h-12 w-full rounded-xl border border-raad-line-10 bg-raad-input px-4 text-base font-semibold placeholder:font-normal placeholder:text-raad-dim focus:border-raad-accent focus:outline-none"
            />

            {error && (
              <p id={errorId} role="alert" className="mt-4 text-sm font-medium text-raad-error">
                {t(`login.error.${error}`)}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="mt-6 min-h-12 w-full rounded-xl bg-raad-primary px-4 text-[15px] font-bold text-raad-onprimary transition-opacity hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? t('login.submitting') : t('login.submit')}
            </button>
          </form>

          {/* REBRAND 28 Oct 2026 — the "New to Raad? / Talk to sales on WhatsApp" block
              is dropped (Mahad); only the quiet "Need help signing in?" support link
              remains below the form. */}
          <p className="mt-7 text-center text-xs text-raad-dim">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
              className="underline decoration-raad-line-20 underline-offset-2 hover:text-raad-text hover:decoration-raad-line-40"
            >
              {t('login.help')}
            </a>
          </p>
        </div>
      </section>

      {/* Intro splash — the centred pulsing arrows mark over the (already-mounted) page
          for a beat, then it lifts. Decorative and pointer-transparent, so it never
          gates the form or intercepts input. Absent under reduced motion. */}
      {intro && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-raad-login-bg"
        >
          <div className="raad-login-pulse">
            <RaadLogo size={76} />
          </div>
        </div>
      )}
    </main>
  );
}
